import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Compass, Send } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PerguntaCard } from './components/PerguntaCard';
import { ProgressoWizard } from './components/ProgressoWizard';

/**
 * Wizard do teste de aptidão: uma pergunta por tela.
 *
 * As respostas ficam só em memória até o envio - não há rascunho no servidor,
 * então sair da página descarta o que foi marcado.
 */
export function AptidaoPage() {
  const navigate = useNavigate();
  const [indice, setIndice] = useState(0);
  const [respostas, setRespostas] = useState<Record<string, string>>({});

  const questionario = trpc.aptidao.obterQuestionario.useQuery();
  const utils = trpc.useUtils();

  const enviar = trpc.aptidao.enviarRespostas.useMutation({
    onSuccess: async (dados) => {
      await utils.aptidao.meuUltimoResultado.invalidate();
      await utils.aptidao.minhasTentativas.invalidate();
      navigate('/aptidao/resultado', { state: { tentativaId: dados.tentativaId } });
    },
  });

  if (questionario.isLoading) {
    return <div className="text-gray-500">Carregando o teste…</div>;
  }

  if (questionario.isError || !questionario.data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        Não foi possível carregar o teste
        {questionario.error?.message ? `: ${questionario.error.message}` : '. Tente recarregar a página.'}
      </div>
    );
  }

  const perguntas = questionario.data.perguntas;
  const pergunta = perguntas[indice]!;
  const respondidas = Object.keys(respostas).length;
  const completo = respondidas === perguntas.length;
  const ultima = indice === perguntas.length - 1;

  const selecionar = (alternativaId: string) => {
    setRespostas((atual) => ({ ...atual, [pergunta.id]: alternativaId }));
  };

  const enviarRespostas = () => {
    enviar.mutate({
      respostas: Object.entries(respostas).map(([perguntaId, alternativaId]) => ({
        perguntaId,
        alternativaId,
      })),
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Compass className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Teste de Aptidão</h1>
        </div>
        <p className="text-sm text-gray-600">
          Responda com sinceridade - no final você descobre com quais diretorias tem mais
          afinidade.
        </p>
      </header>

      <Card>
        <CardContent className="space-y-6 py-6">
          <ProgressoWizard
            atual={indice + 1}
            total={perguntas.length}
            respondidas={respondidas}
          />

          <PerguntaCard
            enunciado={pergunta.enunciado}
            alternativas={pergunta.alternativas}
            selecionada={respostas[pergunta.id]}
            onSelecionar={selecionar}
          />

          {enviar.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {enviar.error.message}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
            <Button
              variant="outline"
              onClick={() => setIndice((i) => Math.max(0, i - 1))}
              disabled={indice === 0}
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>

            {ultima ? (
              <Button
                onClick={enviarRespostas}
                disabled={!completo}
                isLoading={enviar.isPending}
                title={completo ? undefined : 'Responda todas as perguntas para enviar'}
              >
                <Send className="h-4 w-4" />
                Ver meu resultado
              </Button>
            ) : (
              <Button
                onClick={() => setIndice((i) => Math.min(perguntas.length - 1, i + 1))}
                disabled={!respostas[pergunta.id]}
              >
                Avançar
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
